import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { Account } from "types";
import { useGetBranches } from "hooks/useBranches";
import { useNavigate, useParams } from "react-router-dom";

import "react-datepicker/dist/react-datepicker.css";
import "assets/scss/datatables.scss";
import { toast } from "react-toastify";
import { AppRoutes, currencies } from "../../constants";
import { account_types } from "./constants";
import { useGetAccountById, useUpdateAccount } from "hooks/useAccounts";
import { BaseButton } from "Components";
import { accountValidationSchema } from "utils";

const EditAccount = () => {
  document.title = "Pappermap | Edit Account";

  const navigate = useNavigate();
  const { id } = useParams();
  const { mutate, isPending } = useUpdateAccount();
  const { data: accountsData, isLoading } = useGetAccountById(id || "");

  const handleCancel = () => {
    navigate(AppRoutes.ACCOUNTS);
  };

  // add branches, categories, and suppliers
  const { data: branchData } = useGetBranches();

  const initialValues = {
    id: id ? Number(id) : undefined,
    name: "",
    account_number: "",
    description: "",
    account_type: "asset",
    category: "",
    tags: {},
    currency: "USD",
    minimum_balance: 0,
    maximum_balance: 0,
    allow_negative_balance: false,
    daily_transaction_limit: 0,
    monthly_transaction_limit: 0,
    requires_approval: false,
    approval_threshold: 0,
    notes: "",
    custom_fields: {},
    branch_id: 0,
    manager: "",
    balance: 0,
    available_balance: 0,
    is_active: true,
    status: "active",
    organization_id: 0,
    created_by: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    closed_at: undefined,
    last_transaction_date: undefined,
  };

  const formik = useFormik<Partial<Account>>({
    initialValues: initialValues,
    validationSchema: accountValidationSchema,
    onSubmit: async (values) => {
      // Build the payload
      const payload: any = {
        id: values.id,
        name: values.name,
        account_number: values.account_number,
        description: values.description,
        account_type: values.account_type,
        category: values.category,
        tags: values.tags,
        parent_id: values.parent_id,
        currency: values.currency,
        minimum_balance: values.minimum_balance,
        maximum_balance: values.maximum_balance,
        allow_negative_balance: values.allow_negative_balance,
        daily_transaction_limit: values.daily_transaction_limit,
        monthly_transaction_limit: values.monthly_transaction_limit,
        requires_approval: values.requires_approval,
        approval_threshold: values.approval_threshold,
        notes: values.notes,
        custom_fields: values.custom_fields,
        branch_id: values.branch_id,
        // manager: values.manager,
        balance: values.balance || 0,
        available_balance: values.available_balance || 0,
        is_active: true,
        status: "active",
        organization_id: values.organization_id || 0,
        created_by: values.created_by || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        closed_at: values.closed_at || null,
        last_transaction_date: values.last_transaction_date || null,
      };

      // Use the mutate function from your hook
      mutate(
        { id: String(values.id), data: payload },
        {
          onSuccess: (data) => {
            toast.success("Account updated successfully");
            formik.resetForm();

            navigate(AppRoutes.ACCOUNTS);
          },
        }
      );
    },
  });

  useEffect(() => {
    if (accountsData) {
      const account = accountsData;
      formik.setValues({
        id: Number(id),
        name: account.name || "",
        account_number: account.account_number || "",
        description: account.description || "",
        account_type: account.account_type || "asset",
        category: account.category || "",
        tags: account.tags || {},
        currency: account.currency || "USD",
        minimum_balance: account.minimum_balance || 0,
        maximum_balance: account.maximum_balance || 0,
        allow_negative_balance: account.allow_negative_balance || false,
        daily_transaction_limit: account.daily_transaction_limit || 0,
        monthly_transaction_limit: account.monthly_transaction_limit || 0,
        requires_approval: account.requires_approval || false,
        approval_threshold: account.approval_threshold || 0,
        notes: account.notes || "",
        custom_fields: account.custom_fields || {},
        branch_id: account.branch_id || 0,
        // manager: account.manager || 0,
        created_at: account.created_at,
        created_by: account.created_by,
        closed_at: account.closed_at || undefined,
        last_transaction_date: account.last_transaction_date || undefined,
      });
    }
  }, [accountsData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Account" breadcrumbItem="Edit Account" />
          {isLoading ? (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          ) : (
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              <Row>
                <Col xs={12}>
                  {/* Basic Information */}

                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Basic Information</CardTitle>
                      <Form onSubmit={formik.handleSubmit} autoComplete="off">
                        <Row>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="name">Account Name *</Label>
                              <Input
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                placeholder="Enter account name"
                                invalid={
                                  formik.touched.name && !!formik.errors.name
                                }
                              />
                              {formik.touched.name && formik.errors.name && (
                                <FormFeedback>
                                  {formik.errors.name}
                                </FormFeedback>
                              )}
                            </div>
                          </Col>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="account_number">
                                Account number
                              </Label>
                              <Input
                                id="account_number"
                                name="account_number"
                                type="text"
                                value={formik.values.account_number}
                                onChange={formik.handleChange}
                                placeholder="Enter account number"
                                invalid={
                                  formik.touched.account_number &&
                                  !!formik.errors.account_number
                                }
                              />
                              {formik.touched.account_number &&
                                formik.errors.account_number && (
                                  <FormFeedback>
                                    {formik.errors.account_number}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="description">Description</Label>
                              <Input
                                id="description"
                                name="description"
                                type="textarea"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                placeholder="Enter description"
                                invalid={
                                  formik.touched.description &&
                                  !!formik.errors.description
                                }
                              />
                              {formik.touched.description &&
                                formik.errors.description && (
                                  <FormFeedback>
                                    {formik.errors.description}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="branch_id">Branch *</Label>
                              <Input
                                type="select"
                                name="branch_id"
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "branch_id",
                                    e.target.value
                                  )
                                }
                                value={formik.values.branch_id}
                                invalid={
                                  !!(
                                    formik.touched.branch_id &&
                                    formik.errors.branch_id
                                  )
                                }
                              >
                                <option value="">Select Branch...</option>
                                {branchData &&
                                  branchData.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                      {branch.name}
                                    </option>
                                  ))}
                              </Input>
                              {formik.touched.branch_id &&
                                formik.errors.branch_id && (
                                  <FormFeedback>
                                    {formik.errors.branch_id}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="category">Category</Label>
                              <Input
                                type="text"
                                name="category"
                                onChange={formik.handleChange}
                                value={formik.values.category}
                                placeholder="Enter category"
                                invalid={
                                  !!(
                                    formik.touched.category &&
                                    formik.errors.category
                                  )
                                }
                              />
                              {formik.touched.category &&
                                formik.errors.category && (
                                  <FormFeedback>
                                    {formik.errors.category}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="account_type">
                                Account type *
                              </Label>
                              <Input
                                type="select"
                                name="account_type"
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "account_type",
                                    e.target.value
                                  )
                                }
                                value={formik.values.account_type}
                                invalid={
                                  !!(
                                    formik.touched.account_type &&
                                    formik.errors.account_type
                                  )
                                }
                              >
                                <option value="">Select Account type...</option>
                                {account_types &&
                                  account_types.map((type) => (
                                    <option key={type.label} value={type.value}>
                                      {type.label}
                                    </option>
                                  ))}
                              </Input>
                              {formik.touched.account_type &&
                                formik.errors.account_type && (
                                  <FormFeedback>
                                    {formik.errors.account_type}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>

                          <Col sm="6">
                            <div className="mb-3">
                              <Label htmlFor="currency">Currency *</Label>
                              <Input
                                type="select"
                                name="currency"
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "currency",
                                    e.target.value
                                  )
                                }
                                value={formik.values.currency}
                                invalid={
                                  !!(
                                    formik.touched.currency &&
                                    formik.errors.currency
                                  )
                                }
                              >
                                <option value="">Select Currency...</option>
                                {currencies &&
                                  currencies.map((currency) => (
                                    <option
                                      key={currency.label}
                                      value={currency.value}
                                    >
                                      {currency.label}
                                    </option>
                                  ))}
                              </Input>
                              {formik.touched.currency &&
                                formik.errors.currency && (
                                  <FormFeedback>
                                    {formik.errors.currency}
                                  </FormFeedback>
                                )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </CardBody>
                  </Card>

                  {/* Transaction Information */}
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Transaction Information</CardTitle>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="daily_transaction_limit">
                              Daily transaction limit*
                            </Label>
                            <Input
                              id="daily_transaction_limit"
                              name="daily_transaction_limit"
                              type="number"
                              min="0"
                              value={formik.values.daily_transaction_limit}
                              onChange={formik.handleChange}
                              placeholder="Enter daily transaction limit"
                              invalid={
                                formik.touched.daily_transaction_limit &&
                                !!formik.errors.daily_transaction_limit
                              }
                            />
                            {formik.touched.daily_transaction_limit &&
                              formik.errors.daily_transaction_limit && (
                                <FormFeedback>
                                  {formik.errors.daily_transaction_limit}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="monthly_transaction_limit">
                              Monthly transaction limit*
                            </Label>
                            <Input
                              id="monthly_transaction_limit"
                              name="monthly_transaction_limit"
                              min="0"
                              type="number"
                              value={formik.values.monthly_transaction_limit}
                              onChange={formik.handleChange}
                              placeholder="Enter monthly transaction limit"
                              invalid={
                                formik.touched.monthly_transaction_limit &&
                                !!formik.errors.monthly_transaction_limit
                              }
                            />
                            {formik.touched.monthly_transaction_limit &&
                              formik.errors.monthly_transaction_limit && (
                                <FormFeedback>
                                  {formik.errors.monthly_transaction_limit}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="maximum_balance">
                              Maximum balance *
                            </Label>
                            <Input
                              id="maximum_balance"
                              name="maximum_balance"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.maximum_balance}
                              onChange={formik.handleChange}
                              placeholder="Enter maximum balance"
                              invalid={
                                formik.touched.maximum_balance &&
                                !!formik.errors.maximum_balance
                              }
                            />
                            {formik.touched.maximum_balance &&
                              formik.errors.maximum_balance && (
                                <FormFeedback>
                                  {formik.errors.maximum_balance}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="minimum_balance">
                              Minimum balance
                            </Label>
                            <Input
                              id="minimum_balance"
                              name="minimum_balance"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Enter minimum balance"
                              value={formik.values.minimum_balance}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.minimum_balance &&
                                !!formik.errors.minimum_balance
                              }
                            />
                            {formik.touched.minimum_balance &&
                              formik.errors.minimum_balance && (
                                <FormFeedback>
                                  {formik.errors.minimum_balance}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Additional Information */}
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Additional Information</CardTitle>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Input
                              id="requires_approval"
                              name="requires_approval"
                              type="checkbox"
                              checked={formik.values.requires_approval}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.requires_approval &&
                                !!formik.errors.requires_approval
                              }
                            />{" "}
                            <Label htmlFor="requires_approval">
                              Requires approval
                            </Label>
                            {formik.touched.requires_approval &&
                              formik.errors.requires_approval && (
                                <FormFeedback>
                                  {formik.errors.requires_approval}
                                </FormFeedback>
                              )}
                          </div>

                          <div className="mb-3">
                            <Input
                              id="allow_negative_balance"
                              name="allow_negative_balance"
                              type="checkbox"
                              checked={formik.values.allow_negative_balance}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.allow_negative_balance &&
                                !!formik.errors.allow_negative_balance
                              }
                            />{" "}
                            <Label htmlFor="allow_negative_balance">
                              Allow negative balance
                            </Label>
                            {formik.touched.allow_negative_balance &&
                              formik.errors.allow_negative_balance && (
                                <FormFeedback>
                                  {formik.errors.allow_negative_balance}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {/* Button Section */}
              <div className="d-flex justify-content-center mt-3">
                <BaseButton
                  type="submit"
                  color="primary"
                  className="w-20 me-1"
                  style={{ width: "20%" }}
                  loading={isPending}
                >
                  Submit
                </BaseButton>
                <BaseButton
                  color="secondary"
                  className="w-20 ms-1"
                  style={{ width: "20%" }}
                  onClick={handleCancel} // Handle cancel action
                >
                  Cancel
                </BaseButton>
              </div>
            </Form>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EditAccount;
